import {abbreviateList} from 'BenchlingUtil';
import {
  EDITING_SAMPLE_OWNERS_DISABLED_MESSAGE,
  EDITING_SAMPLE_RESTRICTION_STATUS_DISABLED_MESSAGE,
  SampleStatusItem,
} from 'constants/SampleStatusConstants';
import {
  getEditRestrictedSampleUsersDisabledMessage,
  getRestrictedSampleUsersDisplayText,
} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {
  canCurrentUserEditAnyRestrictionStatus,
  canCurrentUserEditRestrictedSampleUsers,
  canCurrentUserEditSampleOwners,
  doesItemSupportSampleStatus,
} from 'inventory/sample-status/SampleStatusPresentationUtils';

function getUpdateSampleRolesDisabledMessage({
  sampleStatusItems,
  isLoading,
  isAuthorized,
  buildSampleStatusNotSupportedMessage,
  buildUnauthorizedMessage,
}: {
  sampleStatusItems: Array<SampleStatusItem & {name: string}>;
  isLoading?: boolean;
  isAuthorized: (sampleStatusItem: SampleStatusItem) => boolean;
  buildSampleStatusNotSupportedMessage: (listOfContainers: Array<string>) => string;
  buildUnauthorizedMessage: (listOfContainers: Array<string>) => string;
}): string | null {
  if (isLoading) {
    return 'Loading...';
  }

  const itemNamesNotSupportingSampleStatus = sampleStatusItems
    .filter((item) => !doesItemSupportSampleStatus(item))
    .map(({name}) => name);
  if (itemNamesNotSupportingSampleStatus.length > 0) {
    return buildSampleStatusNotSupportedMessage(itemNamesNotSupportingSampleStatus);
  }

  const itemNamesWithoutAuthorization = sampleStatusItems
    .filter((item) => !isAuthorized(item))
    .map(({name}) => name);
  if (itemNamesWithoutAuthorization.length > 0) {
    return buildUnauthorizedMessage(itemNamesWithoutAuthorization);
  }

  return null;
}

export function getUpdateSampleRestrictionStatusDisabledMessage({
  sampleStatusItems,
  isLoading,
}: {
  sampleStatusItems: Array<SampleStatusItem & {name: string}>;
  isLoading?: boolean;
}): string | null {
  return getUpdateSampleRolesDisabledMessage({
    isLoading,
    sampleStatusItems,
    isAuthorized: canCurrentUserEditAnyRestrictionStatus,
    buildSampleStatusNotSupportedMessage: (itemNamesNotSupportingSampleStatus: Array<string>) =>
      'Only containers can have restriction statuses',
    buildUnauthorizedMessage: (itemNamesWithoutAuthorization: Array<string>) =>
      `${EDITING_SAMPLE_RESTRICTION_STATUS_DISABLED_MESSAGE} for ${abbreviateList(
        itemNamesWithoutAuthorization,
        3,
        'and'
      )}`,
  });
}

export function getUpdateSampleOwnersDisabledMessage({
  sampleStatusItems,
  isLoading,
}: {
  sampleStatusItems: Array<SampleStatusItem & {name: string}>;
  isLoading?: boolean;
}): string | null {
  return getUpdateSampleRolesDisabledMessage({
    isLoading,
    sampleStatusItems,
    isAuthorized: canCurrentUserEditSampleOwners,
    buildSampleStatusNotSupportedMessage: (itemNamesNotSupportingSampleStatus: Array<string>) =>
      'Only containers can have sample owners',
    buildUnauthorizedMessage: (itemNamesWithoutAuthorization: Array<string>) =>
      `${EDITING_SAMPLE_OWNERS_DISABLED_MESSAGE} for ${abbreviateList(
        itemNamesWithoutAuthorization.map((itemName) => `"${itemName}"`),
        3,
        'and'
      )}`,
  });
}

export function getUpdateRestrictedSampleUsersDisabledMessage({
  sampleStatusItems,
  isLoading,
}: {
  sampleStatusItems: Array<SampleStatusItem & {name: string}>;
  isLoading?: boolean;
}): string | null {
  return getUpdateSampleRolesDisabledMessage({
    isLoading,
    sampleStatusItems,
    isAuthorized: canCurrentUserEditRestrictedSampleUsers,
    buildSampleStatusNotSupportedMessage: (itemNamesNotSupportingSampleStatus: Array<string>) =>
      `Only containers can have ${getRestrictedSampleUsersDisplayText()}`,
    buildUnauthorizedMessage: (itemNamesWithoutAuthorization: Array<string>) =>
      `${getEditRestrictedSampleUsersDisabledMessage()} for ${abbreviateList(
        itemNamesWithoutAuthorization.map((itemName) => `"${itemName}"`),
        3,
        'or'
      )}`,
  });
}
